import React, { useRef } from "react";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import { Download, Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";

function initials(name) {
  const parts = (name || "").split(" ").filter(Boolean);
  return ((parts[0]?.[0] || "F") + (parts[1]?.[0] || "")).toUpperCase();
}

export default function FounderBadgeCard({ founder }) {
  const ref = useRef(null);
  const joined = founder.joined_at ? new Date(founder.joined_at).toLocaleDateString(undefined, { month:"short", year:"numeric" }) : "";

  const generate = async () => {
    if (!ref.current) return null;
    return html2canvas(ref.current, { backgroundColor: null, scale: 2 });
  };

  const download = async () => {
    try {
      const canvas = await generate();
      if (!canvas) return;
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `founder-${founder.founding_number}.png`;
      a.click();
      toast.success("Badge saved.");
    } catch {
      toast.error("Could not generate badge.");
    }
  };

  const nativeShare = async () => {
    try {
      const canvas = await generate();
      if (!canvas) return;
      canvas.toBlob(async (blob) => {
        const file = new File([blob], "founder-badge.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: `Founder #${founder.founding_number} · Powerhouse Refunds`,
            text: `I'm founder #${founder.founding_number} of Powerhouse Refunds — the app that helps people claw back subscription refunds.`,
          });
        } else {
          download();
        }
      });
    } catch {
      download();
    }
  };

  return (
    <div className="space-y-4">
      <div ref={ref} className="relative overflow-hidden rounded-3xl p-8" style={{
        background: "radial-gradient(120% 100% at 100% 0%, #10B981 0%, #059669 40%, #064E3B 100%)",
        color: "white",
        width: "100%",
        maxWidth: 480,
        margin: "0 auto",
        fontFamily: "'Outfit', sans-serif",
      }}>
        <div style={{ position: "absolute", top: 24, right: 24, fontSize: 12, opacity: 0.7, letterSpacing: 2, textTransform: "uppercase" }}>
          Powerhouse Refunds
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.18)", padding: "6px 12px", borderRadius: 999, fontSize: 12, marginBottom: 24 }}>
          <Sparkles style={{ width: 14, height: 14 }} /> THE FOUNDING 100
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 12 }}>
          <div style={{
            width: 96, height: 96, borderRadius: 999,
            background: "rgba(255,255,255,0.18)",
            border: "3px solid rgba(255,255,255,0.5)",
            display: "grid", placeItems: "center",
            fontSize: 36, fontWeight: 700
          }}>{initials(founder.display_name)}</div>
          <div>
            <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1 }}>Founder #{founder.founding_number}</div>
            <div style={{ marginTop: 8, fontSize: 18, opacity: 0.95 }}>{founder.display_name}</div>
            {joined && <div style={{ marginTop: 4, fontSize: 12, opacity: 0.7 }}>Joined {joined}</div>}
          </div>
        </div>
        <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid rgba(255,255,255,0.25)", fontSize: 13, opacity: 0.9 }}>
          One of the first 100 to help people fight bogus subscription charges.
        </div>
      </div>

      <div className="flex gap-2 justify-center flex-wrap">
        <Button onClick={nativeShare} data-testid="founder-badge-share-button" className="rounded-full bg-emerald-600 hover:bg-emerald-700">
          <Share2 className="w-4 h-4 mr-2" /> Share
        </Button>
        <Button onClick={download} variant="outline" data-testid="founder-badge-download-button" className="rounded-full">
          <Download className="w-4 h-4 mr-2" /> Download PNG
        </Button>
      </div>
    </div>
  );
}
